/**
 * Next / previous while an email or an order is open (kanban 97abd058,
 * usage-graph finding C5: 55% of email opens come in runs of three or more,
 * 39% of order opens are followed by another order open).
 *
 * The list page owns the order and filter of its rows and builds a
 * DetailNavigation from them (hooks/useListNavigation.ts); the detail modal
 * renders the buttons (DetailNavButtons.tsx) and binds the keys
 * (hooks/useDetailNavKeys.ts). Everything here is pure so it can be tested
 * without React.
 */

export type NavDirection = 'next' | 'prev';

/** How the person asked to move: the header button or a keyboard shortcut. */
export type NavVia = 'button' | 'keyboard';

export interface DetailNavigation {
  /** 0-based position of the open item in the list, -1 when it is not in it. */
  index: number;
  /** Rows loaded in the list right now. */
  count: number;
  /** Rows the server reports for the list, when known. */
  total: number | null;
  hasPrev: boolean;
  /** True when a later row is loaded OR the server has more rows to load. */
  hasNext: boolean;
  /** A further page is being fetched because Next was pressed at the end. */
  loadingMore: boolean;
  goPrev: (via: NavVia) => void;
  goNext: (via: NavVia) => void;
}

/** The id before/after `currentId` in `ids`, or null at the edge / when absent. */
export function resolveNeighbourId(
  ids: readonly string[],
  currentId: string | null,
  direction: NavDirection,
): string | null {
  if (!currentId) return null;
  const index = ids.indexOf(currentId);
  if (index < 0) return null;
  const target = direction === 'next' ? index + 1 : index - 1;
  if (target < 0 || target >= ids.length) return null;
  return ids[target];
}

/**
 * Shortcuts must not fire while the person is typing. Inputs, textareas,
 * selects and anything content-editable count as typing targets; buttons,
 * links and the page body do not.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((target as HTMLElement).isContentEditable) return true;
  if (target.getAttribute('role') === 'textbox') return true;
  return !!target.closest('[contenteditable=""], [contenteditable="true"]');
}

/**
 * Which way a keydown asks to move, or null when it is not a shortcut.
 * j / ArrowRight = next, k / ArrowLeft = previous (Gmail's j/k plus the
 * arrows people try first). Any modifier means the key belongs to the
 * browser or the OS, so nothing fires.
 */
export function navKeyDirection(e: KeyboardEvent): NavDirection | null {
  if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return null;
  switch (e.key) {
    case 'j':
    case 'J':
    case 'ArrowRight':
      return 'next';
    case 'k':
    case 'K':
    case 'ArrowLeft':
      return 'prev';
    default:
      return null;
  }
}
